import {
    fetchWorkspaces,
    isPathWorkspace,
    logger,
    roseStorage,
    type Workspace,
    workspaceLocation,
} from '@inboundx/shared/platform';
import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_ENDPOINTS, type ApiEndpoint, DEFAULT_ENDPOINT_ID } from '../constants/endpoints';
import { supabase } from '../lib/supabase';

export interface Client {
    /** `public.workspaces.id`. */
    id: string;
    name: string;
    location: string;
    domain: string;
    siteName: string;
    websiteUrl: string;
    pageUrl?: string;
    isLocalPreview: boolean;
}

// Cached lists of domain rows (before workspaces) live under the bare API URL.
const CLIENT_CACHE_PREFIX = 'workspaces:';

/**
 * A bare-host workspace chats as its domain, as domain rows did. A path-rooted one shares
 * its host, so the widget chats as that host (IX-5069). Either way the widget stands in for
 * the root page, which the API routes to the workspace's own knowledge (IX-5167).
 */
function toClient(workspace: Workspace): Client {
    const isPath = isPathWorkspace(workspace);
    const pageUrl = `https://${workspace.host}${workspace.pathPrefix}`;
    const domain = isPath ? workspace.host : workspace.domain;
    return {
        id: workspace.id,
        name: workspace.name,
        location: workspaceLocation(workspace),
        domain,
        siteName: domain,
        websiteUrl: isPath ? pageUrl : `https://www.${domain}`,
        pageUrl,
        isLocalPreview: false,
    };
}

/**
 * Keep the current selection when it is still listed, else default to Abasty or the first.
 * A selection saved before workspaces (IX-5150) is a domain row, whose id is its domain:
 * it moves to the workspace that owns that domain.
 */
function pickSelection(current: Client | null, clients: Client[]): Client | null {
    if (clients.length === 0 || (current && clients.some((c) => c.id === current.id))) {
        return current;
    }
    const legacyDomain = current && current.id === current.domain ? current.domain : null;
    const next =
        clients.find((c) => c.location === legacyDomain) ||
        clients.find((c) => c.name.toLowerCase() === 'abasty') ||
        clients[0];
    roseStorage.setSelectedClient(next);
    return next;
}

declare global {
    interface Window {
        resetInboundXToDefaults?: () => void;
    }
}

interface AppContextType {
    // API Endpoints
    availableEndpoints: ApiEndpoint[];
    selectedEndpoint: ApiEndpoint | null;
    customEndpoint: string;
    useCustomEndpoint: boolean;

    // Clients
    availableClients: Client[];
    selectedClient: Client | null;
    isRefreshingClients: boolean;

    // Actions - API Endpoints
    setSelectedEndpoint: (endpoint: ApiEndpoint) => void;
    setCustomEndpoint: (url: string) => void;
    setUseCustomEndpoint: (use: boolean) => void;

    // Actions - Clients
    setAvailableClients: (clients: Client[]) => void;
    setSelectedClient: (client: Client | null) => void;
    setIsRefreshingClients: (refreshing: boolean) => void;
    fetchClients: (forceRefresh?: boolean) => Promise<void>;
    refreshClients: () => Promise<void>;

    // UI state
    controlsHidden: boolean;
    setControlsHidden: (hidden: boolean) => void;

    // Utility functions
    resetToDefaults: () => void;

    // Computed values
    getCurrentApiUrl: () => string;
    getCurrentEndpointName: () => string;
    getCurrentEndpointId: () => string;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: FC<{ children: ReactNode }> = ({ children }) => {
    // Load initial state from Rose storage
    const [selectedEndpoint, setSelectedEndpointState] = useState<ApiEndpoint | null>(() => {
        try {
            const endpoint = roseStorage.getPreprodEndpoint();
            if (endpoint.selectedId) {
                return (
                    API_ENDPOINTS.find((e) => e.id === endpoint.selectedId) ||
                    API_ENDPOINTS.find((e) => e.id === DEFAULT_ENDPOINT_ID) ||
                    API_ENDPOINTS[0]
                );
            }
        } catch (error) {
            console.error('Failed to load selected endpoint from Rose storage:', error);
        }
        return API_ENDPOINTS.find((e) => e.id === DEFAULT_ENDPOINT_ID) || API_ENDPOINTS[0];
    });

    const [customEndpoint, setCustomEndpointState] = useState<string>(() => {
        try {
            const endpoint = roseStorage.getPreprodEndpoint();
            return endpoint.customUrl || '';
        } catch (error) {
            console.error('Failed to load custom endpoint from Rose storage:', error);
            return '';
        }
    });

    const [useCustomEndpoint, setUseCustomEndpointState] = useState<boolean>(() => {
        try {
            const endpoint = roseStorage.getPreprodEndpoint();
            return endpoint.useCustom || false;
        } catch (error) {
            console.error('Failed to load use custom endpoint from Rose storage:', error);
            return false;
        }
    });

    // Client state
    const [availableClients, setAvailableClients] = useState<Client[]>([]);
    const [selectedClient, setSelectedClientState] = useState<Client | null>(() => {
        try {
            const saved = roseStorage.getSelectedClient();
            // Saved before workspaces (IX-5150): show its domain until pickSelection maps it.
            return saved && { ...saved, location: saved.location ?? saved.domain };
        } catch (error) {
            console.error('Failed to load selected client from Rose storage:', error);
        }
        // Start with null - let fetchClients set the appropriate client
        return null;
    });
    const [isRefreshingClients, setIsRefreshingClients] = useState<boolean>(false);
    const [controlsHidden, setControlsHidden] = useState<boolean>(false);

    // Helper functions for client caching
    const getCachedClients = useCallback((apiUrl: string): Client[] | null => {
        try {
            const cache = roseStorage.getClientCache(`${CLIENT_CACHE_PREFIX}${apiUrl}`);
            if (cache) {
                // Check if cache is still valid (1 hour)
                if (Date.now() - cache.timestamp < 60 * 60 * 1000) {
                    return cache.clients;
                }
            }
        } catch (error) {
            console.warn('Error reading cached clients:', error);
        }
        return null;
    }, []);

    const setCachedClients = useCallback((apiUrl: string, clients: Client[]) => {
        try {
            roseStorage.setClientCache(`${CLIENT_CACHE_PREFIX}${apiUrl}`, {
                timestamp: Date.now(),
                clients: clients,
            });
        } catch (error) {
            console.warn('Error caching clients:', error);
        }
    }, []);

    // Get current API URL
    const getCurrentApiUrl = useCallback((): string => {
        if (useCustomEndpoint && customEndpoint.trim()) {
            return customEndpoint.trim();
        }
        return selectedEndpoint?.url || API_ENDPOINTS[0].url;
    }, [useCustomEndpoint, customEndpoint, selectedEndpoint?.url]); // Only depend on URL, not full object

    // Fetch available clients from API with caching
    const fetchClients = useCallback(
        async (forceRefresh = false) => {
            setIsRefreshingClients(true);
            try {
                const apiUrl = getCurrentApiUrl();

                // Try to use cached clients first unless forcing refresh
                if (!forceRefresh) {
                    const cachedClients = getCachedClients(apiUrl);
                    if (cachedClients) {
                        logger.debug(
                            `Using cached clients for ${apiUrl} (${cachedClients.length} clients)`
                        );
                        setAvailableClients(cachedClients);

                        setSelectedClientState((currentClient) =>
                            pickSelection(currentClient, cachedClients)
                        );
                        setIsRefreshingClients(false);
                        return;
                    }
                }

                logger.debug('Fetching workspaces from Supabase');
                const clients = (await fetchWorkspaces(supabase)).map(toClient);

                logger.debug(`Fetched ${clients.length} clients from Supabase`);

                // Cache the clients
                setCachedClients(apiUrl, clients);
                setAvailableClients(clients);

                setSelectedClientState((currentClient) => pickSelection(currentClient, clients));
            } catch (error) {
                logger.warn('Failed to fetch clients from API:', error);
                setAvailableClients([]);

                // Don't set a default client if API fails - let UI handle empty state
                setSelectedClientState(null);
            } finally {
                setIsRefreshingClients(false);
            }
        },
        [getCurrentApiUrl, getCachedClients, setCachedClients]
    );

    // Refresh clients (force refresh)
    const refreshClients = useCallback(async () => {
        await fetchClients(true);
    }, [fetchClients]);

    // Persist selected endpoint
    const setSelectedEndpoint = (endpoint: ApiEndpoint) => {
        setSelectedEndpointState(endpoint);
        roseStorage.updatePreprodEndpoint({ selectedId: endpoint.id });
        // When selecting a predefined endpoint, disable custom endpoint
        setUseCustomEndpoint(false);
    };

    // Persist custom endpoint
    const setCustomEndpoint = (url: string) => {
        setCustomEndpointState(url);
        roseStorage.updatePreprodEndpoint({ customUrl: url });
    };

    // Persist use custom endpoint
    const setUseCustomEndpoint = (use: boolean) => {
        setUseCustomEndpointState(use);
        roseStorage.updatePreprodEndpoint({ useCustom: use });
    };

    // Client actions
    const setSelectedClient = (client: Client | null) => {
        setSelectedClientState(client);
        roseStorage.setSelectedClient(client);
    };

    // Reset to defaults - clears all Rose preprod storage and resets to default values
    const resetToDefaults = useCallback(() => {
        logger.debug('Resetting to defaults...');
        // Clear all preprod data in Rose storage
        roseStorage.clearPreprodData();

        // Reset state to defaults
        const defaultEndpoint =
            API_ENDPOINTS.find((e) => e.id === DEFAULT_ENDPOINT_ID) || API_ENDPOINTS[0];
        setSelectedEndpointState(defaultEndpoint);
        setCustomEndpointState('');
        setUseCustomEndpointState(false);
        setSelectedClientState(null);
        setAvailableClients([]);
        setIsRefreshingClients(false);

        logger.debug('Reset complete. Default endpoint:', defaultEndpoint);
    }, []);

    // Make resetToDefaults available globally for debugging
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.resetInboundXToDefaults = resetToDefaults;
        }
    }, [resetToDefaults]);

    // Get current endpoint name for display
    const getCurrentEndpointName = useCallback((): string => {
        if (useCustomEndpoint) {
            return 'Custom';
        }
        return selectedEndpoint?.name || 'Unknown';
    }, [useCustomEndpoint, selectedEndpoint?.name]);

    // Get current endpoint ID for status tracking
    const getCurrentEndpointId = useCallback((): string => {
        if (useCustomEndpoint) {
            return 'custom';
        }
        return selectedEndpoint?.id || 'unknown';
    }, [useCustomEndpoint, selectedEndpoint?.id]);

    // Load clients when endpoint changes
    useEffect(() => {
        fetchClients();
    }, [fetchClients]); // fetchClients now has stable dependencies

    const value: AppContextType = {
        // API Endpoints
        availableEndpoints: API_ENDPOINTS,
        selectedEndpoint,
        customEndpoint,
        useCustomEndpoint,
        setSelectedEndpoint,
        setCustomEndpoint,
        setUseCustomEndpoint,

        // Clients
        availableClients,
        selectedClient,
        isRefreshingClients,
        setAvailableClients,
        setSelectedClient,
        setIsRefreshingClients,
        fetchClients,
        refreshClients,

        // UI state
        controlsHidden,
        setControlsHidden,

        // Utility functions
        resetToDefaults,

        // Computed values
        getCurrentApiUrl,
        getCurrentEndpointName,
        getCurrentEndpointId,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
